import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { th, enUS } from "date-fns/locale";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import "./datepicker.css";

const DateImplementationSelector = ({ selectedDate, onDateChange }) => {
  const { t, i18n } = useTranslation("main");
  const [date, setDate] = useState(selectedDate || null);
  const [isOpen, setIsOpen] = useState(false);
  const calendarRef = useRef(null);

  useEffect(() => {
    setDate(selectedDate || null);
  }, [selectedDate]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [calendarRef]);

  // Format the date for display
  const formatSelectedDate = (date) => {
    if (!date) return '';
    
    if (i18n.language === "th") {
      return format(date, "dd/MM/yyyy", { locale: th });
    }
    return format(date, "dd/MM/yyyy", { locale: enUS });
  };

  // Handle date change
  const handleDateChange = (selectedDate) => {
    setDate(selectedDate);
    setIsOpen(false);
    
    if (selectedDate) {
      // Format date in 'YYYY-MM-DD' format when passing to parent
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      onDateChange(formattedDate);
    } else {
      onDateChange('');
    }
  };

  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className="flex items-center justify-between px-3 py-2 bg-vekin-primary rounded-t-md">
      <button
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        type="button"
        className="flex items-center justify-center w-8 h-8 text-white rounded-full hover:bg-vekin-primary/80 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <div className="text-base font-medium text-white">
        {i18n.language === "th" 
          ? format(date, "MMMM", { locale: th }) + " " + (date.getFullYear() + 543)
          : format(date, "MMMM yyyy", { locale: enUS })}
      </div>
      <button
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        type="button"
        className="flex items-center justify-center w-8 h-8 text-white rounded-full hover:bg-vekin-primary/80 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    </div>
  );

  return (
    <div className="relative w-full" id="dateImplementationSelector">
      <div 
        className="flex items-center cursor-pointer text-sm text-vekin-primary focus:outline-0"
        onClick={toggleCalendar}
      >
        {date ? 
          <span className="text-vekin-primary">{formatSelectedDate(date)}</span> : 
          <span className="text-gray-400">{i18n.language === "th" ? "วว/ดด/ปปปป" : "DD/MM/YYYY"}</span>
        }
        <div className="ml-auto">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      </div>
      
      <div ref={calendarRef} className={isOpen ? "block z-60" : "hidden"}>
        <DatePicker
          selected={date}
          onChange={handleDateChange}
          locale={i18n.language === "th" ? th : enUS}
          dateFormat="dd/MM/yyyy"
          renderCustomHeader={renderCustomHeader}
          calendarClassName="custom-calendar shadow-lg border border-gray-200"
          maxDate={new Date()}
          showPopperArrow={false}
          inline
          open={isOpen}
        />
      </div>
    </div>
  );
};

export default DateImplementationSelector; 